import {
  ACCESS_TOKEN_KEY,
  AUTH_STORAGE_KEY,
  REFRESH_TOKEN_KEY,
} from "@/constants/auth.constants";
import { logoutAuth } from "@/features/auth/api/auth-api";

/**
 * Client-side authentication token storage and Authorization header construction.
 */
class AuthService {
  private readonly legacyTokenKey = AUTH_STORAGE_KEY;

  setTokens(accessToken: string, refreshToken?: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    // The refresh token now lives in an HttpOnly cookie the server sets — the login/refresh responses
    // no longer carry it, so refreshToken is normally empty. Only a legacy/OAuth flow that still
    // returns one in the body persists it, and even that is dropped after the first cookie refresh.
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /** Drops any lingering localStorage refresh token once the HttpOnly cookie has taken over rotation. */
  clearLegacyRefreshToken() {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /** @deprecated Prefer JWT login via {@link setTokens}. */
  setToken(token: string) {
    localStorage.setItem(this.legacyTokenKey, token);
  }

  /** @deprecated Prefer JWT login via {@link setTokens}. */
  setBasicCredentials(username: string, password: string) {
    const token = window.btoa(`${username}:${password}`);
    localStorage.setItem(this.legacyTokenKey, token);
    localStorage.setItem("taxos-operator-username", username);
  }

  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Active session token for presence checks (e.g. operator-console gates). Prefers the JWT access
   * token; falls back to the legacy Basic blob. JWT login stores only the access token, so gating on
   * the legacy key alone would wrongly read as "signed out" after a page reload.
   */
  getToken() {
    return this.getAccessToken() ?? localStorage.getItem(this.legacyTokenKey);
  }

  getAuthorizationHeader() {
    const accessToken = this.getAccessToken();
    if (accessToken) {
      return `Bearer ${accessToken}`;
    }
    const legacy = this.getToken();
    return legacy ? `Basic ${legacy}` : "";
  }

  /**
   * Stored sign-in identifier (email/username) for display. Empty when unknown - callers must
   * fall back to session claims, never to a role-implying label like "Tuzidi Operator"
   * (self-serve clients were being shown as operators on their own profile).
   */
  getOperatorUsername() {
    return localStorage.getItem("taxos-operator-username") ?? "";
  }

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.legacyTokenKey);
    localStorage.removeItem("taxos-operator-username");
    // Drop the active-tenant selection too, else the prior user's tenant leaks into the
    // next login on the same browser → X-Active-Tenant → 403 "Not a member of selected tenant".
    localStorage.removeItem("taxos-active-tenant");
  }

  /**
   * Clears session-scoped UI selections that must not survive a change of user - currently the
   * advisor/operator active-tenant. Call on a FRESH human login (login, signup-verify, invite,
   * OAuth), NOT on token refresh: refresh keeps the same principal and must preserve an advisor's
   * acting-as context. Without this, a stale active-tenant leaks into the new user's requests as
   * X-Active-Tenant → 403 "Not a member of selected tenant" (and silently misroutes onboarding saves).
   */
  clearActiveTenantSelection() {
    localStorage.removeItem("taxos-active-tenant");
  }

  /** @deprecated Use {@link clearTokens}. */
  removeToken() {
    this.clearTokens();
  }

  isAuthenticated() {
    return !!this.getAccessToken() || !!this.getToken();
  }

  async logout() {
    try {
      // Server reads the HttpOnly refresh cookie to revoke + clear it; a legacy localStorage token is
      // passed as a fallback so those sessions revoke too.
      await logoutAuth(this.getRefreshToken() ?? undefined);
    } catch {
      // Best-effort server logout; always clear local session.
    }
    this.clearTokens();
  }
}

export const authService = new AuthService();
